import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/store'
import { DashboardInfo } from 'schemas/DashboardInfoSchema'

import { DashboardInfoAdapter } from './dashboardInfoSlice'
import {
  multiCompareByOrder,
  getSortingLevelBySortBy,
  multiFilter,
  compareStringByOrder
} from './utils'
import { CATEGORY, SORT_ORDER } from './constants'

export const {
  selectIds: selectDashboardInfosIds,
  selectEntities: selectDashboardInfosEntities,
  selectAll: selectAllDashboardInfos,
  selectById: selectDashboardInfoById,
  selectTotal: selectDashboardInfosTotal
} = DashboardInfoAdapter.getSelectors<RootState>((state) => state.accessibleDashboards)

export const selectLabelsEntities = (state: RootState) => state.accessibleDashboards.labelsEntities

const selectDashboardInfosByLabel = createSelector(
  [
    (state: RootState) => state.accessibleDashboards.selectedLabels,
    selectLabelsEntities,
    (state: RootState) => state.accessibleDashboards.entities,
    selectAllDashboardInfos
  ],
  (selectedLabels, labelsEntities, entities, allDashboards) => {
    if (selectedLabels.length === 0) return allDashboards

    const dashboardIds = selectedLabels.reduce((acc: number[], label: string) => {
      const entity = labelsEntities[label]
      return entity ? [...acc, ...entity] : acc
    }, [])

    // * prevent duplicated dashboard Id
    const dIdSet = new Set(dashboardIds)
    // * get full dashboard info by dashboard Id
    const dashboards = Array.from(dIdSet).map((dId) => entities[dId!])
    return dashboards
  }
)

export const selectDashboardInfoSortingAndFilter = createSelector(
  [
    (state: RootState) => state.accessibleDashboards.sort,
    (state: RootState) => state.accessibleDashboards.filter
  ],
  (sort, filter) => ({ sort, filter })
)

export const selectDisplayDashboardInfos = createSelector(
  [
    selectDashboardInfosByLabel,
    (state: RootState) => state.accessibleDashboards.sort,
    (state: RootState) => state.accessibleDashboards.filter
  ],
  (dashboards, sort, filter) => {
    const filteredDashboards = (dashboards as DashboardInfo[]).filter(multiFilter(filter))

    const sortingLevel = getSortingLevelBySortBy(sort.sortBy)

    const sortedAndFilteredDashboards = filteredDashboards.sort(
      multiCompareByOrder({ order: sort.order, sortingLevelArr: sortingLevel })
    )

    return sortedAndFilteredDashboards
  }
)

export const selectDashboardInfoHasFilter = createSelector(
  [(state: RootState) => state.accessibleDashboards.filter],
  (filter) => {
    const { keyword, ...otherFilters } = filter
    return Object.values(otherFilters).some((fVal) => fVal === true)
  }
)

export const selectUnselectedLabels = createSelector(
  [selectDisplayDashboardInfos, (state: RootState) => state.accessibleDashboards.selectedLabels],
  (displayDashboards, selectedLabels) => {
    const labelsArr = displayDashboards.reduce(
      (acc: string[], dashboard) => (dashboard.labels ? acc.concat(dashboard.labels) : acc),
      [] as string[]
    )

    // * remove duplicated labels
    const labelsSet = new Set(labelsArr)

    // * remove selected labels
    selectedLabels.forEach((label) => {
      labelsSet.delete(label)
    })
    // * sort labels by name
    const labels = Array.from(labelsSet).sort(compareStringByOrder(SORT_ORDER.ASC))
    return labels
  }
)

export const selectSelectedLabels = (state: RootState) => state.accessibleDashboards.selectedLabels

export const selectHasLabels = createSelector(
  [selectLabelsEntities],
  (labelsEntities) => Object.keys(labelsEntities).length > 0
)

export const selectDashboardInfoStatus = (state: RootState) => state.accessibleDashboards.status

export const selectIsDashboardCopying = (state: RootState) =>
  state.dashboard.operatingStatus === 'copying'

export const selectIsDashboardDeleting = (state: RootState) =>
  state.dashboard.operatingStatus === 'deleting'

export const selectHasError = createSelector(
  [selectDashboardInfoStatus, selectDashboardInfosTotal],
  (status, dashboardsCount) =>
    status === 'failed' || (status === 'succeeded' && dashboardsCount === 0)
)

export const selectDashboardInfoCategory = (state: RootState) => state.accessibleDashboards.category

export const selectCategoryIsTemplate = (state: RootState) => {
  const { category } = state.accessibleDashboards
  return category === CATEGORY.myTemplates || category === CATEGORY.companyTemplates
}

export const selectMemorizedStateForV4 = createSelector(
  [(state: RootState) => state.accessibleDashboards, (state: RootState) => state.ui],
  (accessibleDashboards, ui) => {
    const { sort, filter, selectedLabels, category } = accessibleDashboards
    const { dashboardViewLayout } = ui
    return {
      accessibleDashboards: {
        sort,
        filter,
        selectedLabels,
        category
      },
      ui: {
        dashboardViewLayout
      }
    }
  }
)

export const selectIsFiltered = createSelector(
  [(state: RootState) => state.accessibleDashboards.filter],
  (filter) => {
    const { keyword, ...otherFilters } = filter
    return Object.values(otherFilters).some((fVal) => fVal === true) || !!keyword
  }
)
